import images from "../../assets/images";
import { useCallback, useMemo, useEffect, useState } from "react";
import { useTable, useFlexLayout, useSortBy } from "react-table";
import { FixedSizeList } from "react-window";
import { useScrollbarWidth } from "../../hooks/useScrollbarWidth";
import { useMedals } from "../../hooks/useMedals";
import "./index.css";

const calculateTableHeight = (isLong, isCanadaTop, height, width) => {
  if (isLong) {
    const bannerHeight = (width / 10) * 2.25 + 25;
    return height - bannerHeight;
  }
  if (isCanadaTop) {
    return 130;
  }
  return 110;
};

const Table = ({
  columns,
  data,
  language,
  type,
  height,
  width,
  size,
  defaultSort,
}) => {
  const defaultColumn = useMemo(
    () => ({ minWidth: 1, width: 1, maxWidth: 10, padding: 0 }),
    []
  );
  const memoizedSortBy = useMemo(() => {
    return defaultSort === "gold"
      ? [
          { id: "medals.gold", desc: false },
          { id: "medals.silver", desc: false },
          { id: "medals.bronze", desc: false },
          {
            id:
              language === "fr"
                ? "organisation.description_fr"
                : "organisation.description",
            desc: false,
          },
        ]
      : [
          { id: "medals.total", desc: false },
          { id: "medals.gold", desc: false },
          { id: "medals.silver", desc: false },
          { id: "medals.bronze", desc: false },
          {
            id:
              language === "fr"
                ? "organisation.description_fr"
                : "organisation.description",
            desc: false,
          },
        ];
  }, [defaultSort, language]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setSortBy,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableSortRemove: true,
      initialState: {
        sortBy: memoizedSortBy,
      },
    },
    useFlexLayout,
    useSortBy
  );

  useEffect(() => {
    setSortBy(
      defaultSort === "gold"
        ? [
            { id: "medals.gold", desc: false },
            { id: "medals.silver", desc: false },
            { id: "medals.bronze", desc: false },
            {
              id:
                language === "fr"
                  ? "organisation.description_fr"
                  : "organisation.description",
              desc: false,
            },
          ]
        : [
            { id: "medals.total", desc: false },
            { id: "medals.gold", desc: false },
            { id: "medals.silver", desc: false },
            { id: "medals.bronze", desc: false },
            {
              id:
                language === "fr"
                  ? "organisation.description_fr"
                  : "organisation.description",
              desc: false,
            },
          ]
    );
  }, [defaultSort, language, setSortBy]);

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];
      const isCanada = row.values["organisation.code"] === "CAN";
      const className = isCanada ? "tr Canada" : "tr";
      prepareRow(row);
      return (
        <div {...row.getRowProps({ style })} className={className}>
          {row.cells.map((cell) => {
            const cellClassName =
              cell.column.id === "organisation.description"
                ? "td-country"
                : "td";
            return (
              <div {...cell.getCellProps()} className={cellClassName}>
                {cell.column.id === "medals.total" ? (
                  <>{row.values[cell.column.id]}</>
                ) : (
                  cell.render("Cell", { state })
                )}
              </div>
            );
          })}
        </div>
      );
    },
    [prepareRow, rows]
  );

  const canadaRow = rows.find(
    (row) => row.values["organisation.code"] === "CAN"
  );
  if (canadaRow) {
    prepareRow(canadaRow);
  }

  return (
    <div {...getTableProps()} className="innerTable">
      <div className="parentRowgroup" style={{ width: width }}>
        {headerGroups.map((headerGroup) => (
          <div {...headerGroup.getHeaderGroupProps()} className="tr">
            {headerGroup.headers
              .filter((column) => column.Header !== "")
              .map((column) => {
                const orderBy = column.id.substring(column.id.indexOf(".") + 1);
                return (
                  <div
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="tableHeight"
                    id={
                      column.Cell.name === "renderCountryNameCell"
                        ? "countryHeader"
                        : ""
                    }
                    title=""
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
      {type === "canada" && canadaRow && (
        <div className="tr sticky-row Canada" style={{ width: width - 100 }}>
          {canadaRow.cells.map((cell) => {
            const cellClassName =
              cell.column.id === "organisation.description"
                ? "td-country"
                : "td";
            return (
              <div {...cell.getCellProps()} className={cellClassName}>
                {cell.column.id === "medals.total" ? (
                  <>{cell.value}</>
                ) : (
                  cell.render("Cell", { state })
                )}
              </div>
            );
          })}
        </div>
      )}
      <div {...getTableBodyProps()} className="rowGroup">
        <div className="list">
          <FixedSizeList
            height={
              size === "short" && type === "canada" ? height - 20 : height
            }
            itemCount={rows.length && size !== "long" ? 5 : rows.length}
            itemSize={22}
            width={width}
          >
            {RenderRow}
          </FixedSizeList>
        </div>
      </div>
    </div>
  );
};

export const MedalTable = ({
  defaultSort,
  type,
  language,
  size,
  tableWidth,
  tableHeight,
}) => {
  const { medals } = useMedals();
  useEffect(() => {}, [medals]);
  useEffect(() => {}, [language]);

  const height = useMemo(() => {
    return calculateTableHeight(
      size === "long",
      type === "canada",
      tableHeight,
      tableWidth
    );
  }, [size, tableHeight, tableWidth, type]);

  const data = useMemo(() => {
    return (medals || []).map((medal) => ({
      ...medal,
      medals: {
        ...medal.medals,
        total: medal.medals.total || 0,
      },
    }));
  }, [medals]);

  const renderCountryHeader = useCallback(
    ({ column }) => {
      let countryHeader = language !== "fr" ? "COUNTRY" : "PAYS"
      return (
      <div id="countryHeader" title={countryHeader}>
        {countryHeader}
        <span className="sortArrow" title="Sort arrow">
          {column.sortedIndex === 0 && column.isSorted
            ? column.sortedIndex === 0 && column.isSortedDesc
              ? "\u2191"
              : "\u2193"
            : ""}
        </span>
      </div>
    )},
    [language]
  );

  const renderMedalHeader = useCallback(
    (medalType) =>
      ({ column }) => {
        let src = images.get(medalType);
        let medalTitle = language !== "fr" ? medalType.charAt(0).toUpperCase() + medalType.slice(1) :
          medalType === 'gold' ? 'Or' :
          medalType === 'silver' ? 'Argent' :
          'Bronze';
        return (
          <div className={`medalHeader ${medalType}Header`} title={medalTitle}>
            <div className="MedalButton" title={medalTitle}>
              <img className="medal" src={src} alt={`${medalType} medal`} />
            </div>
            <span className="sortArrow" title="Sort arrow">
              {column.sortedIndex === 0 && column.isSorted
                ? column.sortedIndex === 0 && column.isSortedDesc
                  ? "\u2191"
                  : "\u2193"
                : ""}
            </span>
          </div>
        );
      },
    []
  );

  const renderTotalHeader = useCallback(
    ({ column }) => (
      <div className="totalHeader" title="Total">
        <span className="totalButton">T</span>
        <span className="sortArrow" title="Sort arrow">
          {column.sortedIndex === 0 && column.isSorted
            ? column.sortedIndex === 0 && column.isSortedDesc
              ? "\u2191"
              : "\u2193"
            : ""}
        </span>
      </div>
    ),
    []
  );

  const renderRankCell = useCallback(({ cell, row, state }) => {
    const { sortBy } = state;
    const currentSortColumn = sortBy.length > 0 ? sortBy[0].id : "rank";
    let rankValue = row.original.rank;
    if (currentSortColumn.includes("gold")) {
      rankValue = row.original.goldRank;
    } else if (currentSortColumn.includes("silver")) {
      rankValue = row.original.silverRank;
    } else if (currentSortColumn.includes("bronze")) {
      rankValue = row.original.bronzeRank;
    }
    return (
      <span className="rank" id="rank">
        {rankValue}
      </span>
    );
  }, []);

  const renderCountryCell = useCallback(({ cell }) => {
    let src = images.get(cell.value) || images.get("NOFLAG");
    return (
      <div className="flag-container">
        <img className="flag" src={src} alt={cell.value} />
      </div>
    );
  }, []);
  const renderCountryNameCell = useCallback(
    ({ cell }) => {
      const lang =
        language !== "fr"
          ? cell.value
          : cell.row.original.organisation.description_fr || cell.value;
      return (
        <span className="countryName" title={lang}>
          {lang}
        </span>
      );
    },
    [language]
  );
  const renderMedalCell = useCallback(
    (medalType) =>
      ({ cell }) => {
        if (cell.row.original.organisation["code"] === "CAN") {
          if (!cell.row.original.medals) {
          } else if (!cell.row.original.medals[medalType]) {
            console.error(
              `Medals data for ${medalType} is missing for Canada:`,
              cell.row.original.medals
            );
          }
        }
        const medals = cell.row.original.medals;
        const displayValue = medals ? medals[medalType] : "";
        return <div className={`cell ${medalType}Cell`}>{displayValue}</div>;
      },
    []
  );

  const sortOrder = useMemo(
    () => (medalTypes) => (a, b, id, desc) => {
      const aMedals = a.original.medals;
      const bMedals = b.original.medals;

      for (const medalType of medalTypes) {
        if (aMedals[medalType] !== bMedals[medalType]) {
          return aMedals[medalType] > bMedals[medalType] ? -1 : 1;
        }
      }
      const aDescription =
        language === "fr"
          ? a.original.organisation.description_fr
          : a.original.organisation.description;
      const bDescription =
        language === "fr"
          ? b.original.organisation.description_fr
          : b.original.organisation.description;
  
      return aDescription.localeCompare(bDescription, language);
    },
    [language]
  );
  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "goldRank",
        Cell: (props) => renderRankCell({ ...props }),
        sortDescFirst: false,
        disableSortBy: true,
      },
      {
        Header: "",
        accessor: "organisation.code",
        Cell: renderCountryCell,
        disableSortBy: true,
      },
      {
        Header: renderCountryHeader,
        accessor: "organisation.description",
        Cell: renderCountryNameCell,
        width: 5,
        sortType: sortOrder([]),
      },
      {
        Header: renderMedalHeader("gold"),
        accessor: "medals.gold",
        sortDescFirst: false,
        Cell: renderMedalCell("gold"),
        sortType: sortOrder(["gold", "silver", "bronze"]),
      },
      {
        Header: renderMedalHeader("silver"),
        accessor: "medals.silver",
        sortDescFirst: false,
        Cell: renderMedalCell("silver"),
        sortType: sortOrder(["silver", "gold", "bronze"]),
      },
      {
        Header: renderMedalHeader("bronze"),
        accessor: "medals.bronze",
        sortDescFirst: false,
        Cell: renderMedalCell("bronze"),
        sortType: sortOrder(["bronze", "gold", "silver"]),
      },
      {
        Header: renderTotalHeader,
        accessor: "medals.total",
        sortDescFirst: false,
        Cell: renderMedalCell("total"),
        sortType: sortOrder(["total", "gold", "silver", "bronze"]),
      },
    ],
    [
      language,
      renderCountryCell,
      renderCountryHeader,
      renderCountryNameCell,
      renderMedalCell,
      renderMedalHeader,
      renderRankCell,
      renderTotalHeader,
    ]
  );

  const scrollbarWidth = useScrollbarWidth();

  return (
    <div className="table">
      <div className="banner">
        <img
          className="bannerImage OlyBanner"
          src={language !== "fr" ? images.get("OLY1") : images.get("OLY2")}
          alt="Paris 2024"
        />
      </div>
      <Table
        className="table"
        columns={columns}
        data={data}
        type={type}
        height={height}
        width={tableWidth - scrollbarWidth}
        size={size}
        language={language}
        defaultSort={defaultSort}
      />
      <div className="footer">
        <img
          className={`footerImage ${size === "long" && "LongBanner"}`}
          src={language !== "fr" ? images.get("CP1") : images.get("CP2")}
          alt={
            language !== "fr" ? "The Canadian Press" : "La Presse Canadienne"
          }
        />
      </div>
    </div>
  );
};
